import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Drawers abertos, do mais antigo ao mais recente: ESC fecha só o do topo.
const openStack = [];

/**
 * Drawer Component - Painel lateral deslizante
 * 
 * @param {boolean} open - Se o drawer está aberto
 * @param {Function} onClose - Callback ao fechar (botão X, botões de ação e ESC; clique fora não fecha)
 * @param {string|React.ReactNode} title - Título do drawer (string ou conteúdo rico)
 * @param {string} size - Tamanho: 'sm', 'compact', 'md', 'lg', 'xl', '2xl', 'full'
 * @param {ReactNode} children - Conteúdo do drawer
 * @param {ReactNode} footer - Footer customizado (opcional)
 */
function Drawer({
    open,
    onClose,
    title,
    size = 'md',
    children,
    footer = null
}) {
    const tokenRef = useRef({});
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Fecha ao pressionar ESC, apenas o drawer do topo e se ninguém tratou a tecla antes
    useEffect(() => {
        if (!open) return undefined;
        const token = tokenRef.current;
        openStack.push(token);
        document.body.style.overflow = 'hidden';
        const handleEscape = (e) => {
            if (e.key === 'Escape' && !e.defaultPrevented && openStack[openStack.length - 1] === token) {
                onCloseRef.current();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
            const index = openStack.indexOf(token);
            if (index >= 0) openStack.splice(index, 1);
            if (openStack.length === 0) document.body.style.overflow = 'unset';
        };
    }, [open]);

    const sizeClasses = {
        sm: 'max-w-md',
        compact: 'max-w-xl',
        md: 'max-w-2xl',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
        '2xl': 'max-w-7xl',
        full: 'max-w-full',
    };

    if (!open) return null;

    const drawer = (
        <div className="fixed inset-0 z-[100] overflow-hidden">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"></div>

            {/* Drawer Panel */}
            <div className="fixed inset-y-0 right-0 flex max-w-full">
                <div className={`relative w-screen ${sizeClasses[size]}`}>
                    <div className="flex h-full flex-col bg-white shadow-xl">
                        {/* Header */}
                        <div className="flex shrink-0 items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <div className="flex-1 min-w-0 pr-2">
                                {typeof title === 'string' ? (
                                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                                ) : (
                                    <div className="text-gray-900">{title}</div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            {children}
                        </div>

                        {/* Footer */}
                        {footer && (
                            <div className="shrink-0 border-t border-gray-200 px-6 py-4 bg-gray-50">
                                {footer}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(drawer, document.body);
}

export default Drawer;

